import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaChevronLeft,
  FaChevronRight,
  FaClipboardList,
  FaEnvelopeOpenText,
  FaExternalLinkAlt,
  FaMapMarkerAlt,
  FaPlus,
  FaTable,
  FaTh,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import HomeClampedText from '../../components/HomeClampedText';
import { fetchMyPublishedInfrastructureListings } from '../../services/infrastructureLeasingListing.service';
import { resolvePublicFileUrl } from '../../constants/api-urls';
import { ILE_TRANSACTION_TYPES } from '../../utils/infrastructureLeasingSearchConfig';
import { getOwnerInventorySnapshotLine, getSearchCardAvailabilityBadge } from '../../utils/infrastructureListingInventoryAvailabilityUi';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/infrastructure_search_listings.scss';
import '../../assets/styles/infrastructure_leasing_my_properties.scss';

const LAYOUT_STORAGE_KEY = 'ile_my_properties_layout';

const listingTabForDetail = (item) =>
  item.listingType === 'machinery' ? 'equipment' : 'real-estate';

const typeLabel = (item) =>
  item.listingType === 'machinery' ? 'Machinery / asset' : item.listingType === 'property' ? 'Property' : '—';

const txClass = (tx) => {
  if (tx === 'lease' || tx === 'lease_to_own') return 'ile-search-badge--lease';
  if (tx === 'rent') return 'ile-search-badge--rent';
  if (tx === 'sale') return 'ile-search-badge--sale';
  return 'ile-search-badge--default';
};

const txLabel = (tx) =>
  (ILE_TRANSACTION_TYPES.find((k) => k.value === (tx || 'lease')) || { label: 'Lease' }).label;

function listingHasEnquiries(item) {
  const n = Number(item?.enquiryCount);
  return Number.isFinite(n) && n > 0;
}

function formatUpdated(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function readStoredLayout() {
  try {
    const v = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (v === 'table' || v === 'cards') return v;
  } catch {
    /* ignore */
  }
  return 'cards';
}

export default function InfrastructureLeasingMyPropertiesPage() {
  const [page, setPage] = useState(1);
  const pageSize = 12;
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(readStoredLayout);

  useEffect(() => {
    try {
      localStorage.setItem(LAYOUT_STORAGE_KEY, viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchMyPublishedInfrastructureListings({ page, pageSize });
      if (res?.success && res.data) {
        const d = res.data;
        setItems(Array.isArray(d.items) ? d.items : []);
        setTotal(Number(d.total) || 0);
        setTotalPages(Number(d.totalPages) || 0);
      } else {
        setItems([]);
        setTotal(0);
        setTotalPages(0);
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message;
      toast.error(msg || 'Could not load your listings.');
      setItems([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const rangeLabel = useMemo(() => {
    if (total === 0) return '0 results';
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);
    return `Showing ${start}–${end} of ${total}`;
  }, [total, page, pageSize]);

  const goPage = (next) => {
    if (next < 1 || (totalPages > 0 && next > totalPages)) return;
    setPage(next);
  };

  return (
    <div className="container-fluid ile-my-properties py-4">
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item">
            <Link to="/dashboard/seller">Seller dashboard</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            My properties / assets
          </li>
        </ol>
      </nav>

      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h1 className="h3 mb-1">My properties / assets</h1>
          <p className="text-muted mb-0 small">
            Published listings from your account. Only live (published) assets appear here.
          </p>
        </div>
        <div className="d-flex flex-wrap gap-2 justify-content-end">
          <Link
            to="/infrastructure-leasing/my-properties/inventory-overview"
            className="btn btn-outline-dark d-inline-flex align-items-center gap-2"
          >
            <FaClipboardList aria-hidden />
            Inventory operations
          </Link>
          <Link
            to="/infrastructure-leasing/list-asset"
            className="btn btn-dark d-inline-flex align-items-center gap-2"
          >
            <FaPlus aria-hidden />
            Create new listing
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="text-muted py-5">Loading your listings…</div>
      ) : items.length === 0 ? (
        <div className="border rounded p-5 text-center bg-white ile-my-properties-empty">
          <p className="mb-3 text-muted">You don’t have any published listings yet.</p>
          <Link to="/infrastructure-leasing/list-asset" className="btn btn-primary">
            List an asset
          </Link>
        </div>
      ) : (
        <>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3 ile-my-properties-toolbar">
            <span className="small text-muted">{rangeLabel}</span>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="small text-muted d-none d-sm-inline">Layout</span>
              <div className="ile-search-view-toggles" role="group" aria-label="Choose layout">
                <button
                  type="button"
                  className={viewMode === 'cards' ? 'active' : ''}
                  onClick={() => setViewMode('cards')}
                  title="Cards"
                  aria-pressed={viewMode === 'cards'}
                  aria-label="Card layout"
                >
                  <FaTh aria-hidden />
                </button>
                <button
                  type="button"
                  className={viewMode === 'table' ? 'active' : ''}
                  onClick={() => setViewMode('table')}
                  title="Table"
                  aria-pressed={viewMode === 'table'}
                  aria-label="Table layout"
                >
                  <FaTable aria-hidden />
                </button>
              </div>
            </div>
          </div>

          {viewMode === 'cards' ? (
            <div className="row g-4 ile-my-properties-grid">
              {items.map((item) => {
                const invSnap = getOwnerInventorySnapshotLine(item.inventoryAvailability);
                return (
                <div key={item.id} className="col-12 col-md-6 col-xl-4 d-flex">
                  <div className="ile-search-card w-100">
                    <div className="ile-search-card__img">
                      <img
                        src={
                          item.imagePath
                            ? resolvePublicFileUrl(item.imagePath)
                            : 'https://via.placeholder.com/400x240?text=Listing'
                        }
                        alt=""
                        onError={(e) => {
                          e.currentTarget.src = 'https://via.placeholder.com/400x240?text=Photo';
                        }}
                      />
                      <div className="ile-search-card__badges">
                        <div className="d-flex flex-column gap-1 align-items-start">
                          {(() => {
                            const ab = getSearchCardAvailabilityBadge(item);
                            return ab ? (
                              <span className={`ile-search-badge ${ab.className}`}>{ab.label}</span>
                            ) : null;
                          })()}
                          <span className="ile-search-badge ile-search-badge--ok text-capitalize">
                            {(item.listingStatus || 'published').replace(/_/g, ' ')}
                          </span>
                        </div>
                        <div>
                          <span className={`ile-search-badge ${txClass(item.transactionType || 'lease')}`}>
                            {txLabel(item.transactionType)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="ile-search-card__body">
                      <p className="small text-muted mb-1">{typeLabel(item)}</p>
                      <h2 className="h5">{item.title}</h2>
                      {item.locationLine && item.locationLine !== '—' && (
                        <p className="ile-search-loc m-0">
                          <FaMapMarkerAlt className="mt-1" style={{ flexShrink: 0, color: '#9ca3af' }} />
                          {item.locationLine}
                        </p>
                      )}
                      <div className="ile-search-price">{item.pricePrimary || item.price}</div>
                      {invSnap && (
                        <p className="small text-muted mb-1" title="From tracked inventory">
                          {invSnap}
                        </p>
                      )}
                      {item.keySpec && item.keySpec !== '—' && (
                        <p className="ile-search-spec mb-0">{item.keySpec}</p>
                      )}
                      {item.description ? (
                        <HomeClampedText
                          as="p"
                          omitBaseClass
                          className="ile-search-desc"
                          text={String(item.description)}
                        />
                      ) : null}
                    </div>
                    <div className="ile-search-card__foot">
                      <span>Updated</span>
                      <span className="ile-search-cat text-truncate" style={{ maxWidth: '50%' }} title={item.categoryLabel}>
                        {item.categoryLabel || '—'}
                      </span>
                    </div>
                    <div className="px-3 pb-3 d-grid gap-2">
                      <Link
                        to={`/infrastructure-leasing/listing/${item.id}?tab=${listingTabForDetail(item)}`}
                        className="btn w-100 text-decoration-none"
                        style={{
                          background: '#000',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 8,
                          padding: '10px',
                        }}
                      >
                        View listing
                      </Link>
                      <Link
                        to={`/infrastructure-leasing/my-properties/${item.id}/inventory`}
                        className="btn btn-outline-dark w-100 text-decoration-none"
                        style={{ borderRadius: 8, padding: '10px' }}
                      >
                        <FaClipboardList className="me-2" aria-hidden />
                        Inventory hub
                      </Link>
                      {listingHasEnquiries(item) ? (
                        <Link
                          to={`/infrastructure-leasing/my-properties/${item.id}/enquiries`}
                          className="btn btn-outline-dark w-100 d-inline-flex align-items-center justify-content-center gap-2"
                          style={{ borderRadius: 8, padding: '10px' }}
                        >
                          <FaEnvelopeOpenText aria-hidden />
                          View enquiries
                        </Link>
                      ) : null}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="table-responsive border rounded bg-white ile-my-properties-table-wrap">
              <table className="table table-hover align-middle mb-0 ile-my-properties-table">
                <thead className="table-light">
                  <tr>
                    <th scope="col" className="text-muted small text-uppercase">
                      Asset
                    </th>
                    <th scope="col" className="text-muted small text-uppercase">
                      Type
                    </th>
                    <th scope="col" className="text-muted small text-uppercase">
                      Category
                    </th>
                    <th scope="col" className="text-muted small text-uppercase">
                      Location
                    </th>
                    <th scope="col" className="text-muted small text-uppercase">
                      Status
                    </th>
                    <th scope="col" className="text-muted small text-uppercase">
                      Price
                    </th>
                    <th scope="col" className="text-muted small text-uppercase">
                      Updated
                    </th>
                    <th scope="col" className="text-end text-muted small text-uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const invSnapTable = getOwnerInventorySnapshotLine(item.inventoryAvailability);
                    return (
                    <tr key={item.id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="ile-my-properties-thumb flex-shrink-0">
                            <img
                              src={
                                item.imagePath
                                  ? resolvePublicFileUrl(item.imagePath)
                                  : 'https://via.placeholder.com/56?text=–'
                              }
                              alt=""
                              onError={(e) => {
                                e.currentTarget.src = 'https://via.placeholder.com/56?text=–';
                              }}
                            />
                          </div>
                          <div>
                            <span className="fw-medium d-block">{item.title}</span>
                            {invSnapTable && (
                              <span className="small text-muted d-block mt-1">{invSnapTable}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{typeLabel(item)}</td>
                      <td className="text-truncate" style={{ maxWidth: 140 }} title={item.categoryLabel}>
                        {item.categoryLabel || '—'}
                      </td>
                      <td className="small">
                        {item.locationLine && item.locationLine !== '—' ? (
                          <span className="d-flex align-items-start gap-1">
                            <FaMapMarkerAlt className="mt-1 flex-shrink-0 text-secondary" />
                            <span>{item.locationLine}</span>
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <span className="badge text-bg-success text-capitalize">
                          {(item.listingStatus || 'published').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="small fw-semibold">{item.pricePrimary || item.price || '—'}</td>
                      <td className="small text-muted">{formatUpdated(item.updatedAt)}</td>
                      <td className="text-end text-nowrap">
                        <div className="d-inline-flex flex-column flex-sm-row gap-1 align-items-stretch align-items-sm-center justify-content-end">
                          <Link
                            to={`/infrastructure-leasing/listing/${item.id}?tab=${listingTabForDetail(item)}`}
                            className="btn btn-sm btn-outline-dark"
                          >
                            View listing <FaExternalLinkAlt className="ms-1" size={12} aria-hidden />
                          </Link>
                          <Link
                            to={`/infrastructure-leasing/my-properties/${item.id}/inventory`}
                            className="btn btn-sm btn-outline-secondary"
                          >
                            <FaClipboardList className="me-1" aria-hidden />
                            Inventory
                          </Link>
                          {listingHasEnquiries(item) ? (
                            <Link
                              to={`/infrastructure-leasing/my-properties/${item.id}/enquiries`}
                              className="btn btn-sm btn-dark"
                            >
                              <FaEnvelopeOpenText className="me-1" aria-hidden />
                              Enquiries
                            </Link>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <nav
              className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-4 ile-my-properties-pagination"
              aria-label="Listing pages"
            >
              <span className="small text-muted">{rangeLabel}</span>
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page <= 1}
                  onClick={() => goPage(page - 1)}
                  aria-label="Previous page"
                >
                  <FaChevronLeft aria-hidden />
                </button>
                <span className="btn btn-sm btn-outline-secondary disabled text-dark">
                  Page {page} / {totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page >= totalPages}
                  onClick={() => goPage(page + 1)}
                  aria-label="Next page"
                >
                  <FaChevronRight aria-hidden />
                </button>
              </div>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
